import { FC, useEffect, useState } from 'react'

import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from '@inno-ui/button'
import { ContextMenu, ContextMenuTriggerOnActions } from '@inno-ui/context-menu'
import {
  ExclamationTriangleLineIcon,
  IconSizes,
  IconWeight,
  MicrophoneLineIcon,
  MultiplyIcon,
  SquareFullIcon
} from '@inno-ui/icons'
import { Tooltip } from '@inno-ui/tooltip'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { QuickActionButton } from 'shared/components/QuickActionButton'
import { useSttStore } from 'shared/stores/sttStore'

import { useFocusRestore } from '../../hooks/useFocusRestore'
import { useSttFocusTracking } from '../../hooks/useSttFocusTracking'
import { useSttMenuOptions } from '../../hooks/useSttMenuOptions'
import { useSttResult } from '../../hooks/useSttResult'
import { useSttStream } from '../../hooks/useSttStream'
import { useSttWaveform } from '../../hooks/useSttWaveform'
import styles from './SttQuickAction.module.scss'
import { WAVEFORM_WIDTH } from './waveform'

export const SttQuickAction: FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'stt' })

  const {
    start,
    stop,
    cancel,
    status,
    segments,
    analyserNode,
    error,
    resetError
  } = useSttStream()

  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false)
  const [hovered, setHovered] = useState(false)

  const isRecording = status === 'recording'
  const isFinalizing = status === 'finalizing'
  const isError = status === 'error'
  const isActive = isRecording || isFinalizing
  const showCanvas = isActive || isError

  const { prevActiveElementRef, visible } =
    useSttFocusTracking(isContextMenuOpen)
  const { canvasRef } = useSttWaveform(isRecording, analyserNode, showCanvas)
  const { restoreFocus } = useFocusRestore(
    isContextMenuOpen,
    prevActiveElementRef
  )
  const { language, targetLanguage, menuOptions } = useSttMenuOptions(
    restoreFocus,
    setIsContextMenuOpen
  )
  const isFocusInsertable = useSttStore(state => state.isFocusInsertable)
  useSttResult(status, segments)

  useEffect(() => {
    if (!visible) {
      if (isActive) cancel()
      if (isError) resetError()
    }
  }, [visible, isActive, isError, cancel, resetError])

  useEffect(() => {
    setHovered(false)
  }, [isFocusInsertable])

  const handleClick = () => {
    if (isActive) return
    void start({ language, targetLanguage })
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation()
    cancel()
  }

  const handleStop = (e: React.MouseEvent) => {
    e.stopPropagation()
    stop()
  }

  const button = (
    <QuickActionButton
      color='#2aba7e'
      className={styles.sttQuickAction}
      onClick={handleClick}
      onMouseEnter={() => !isActive && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseDown={e => {
        if (e.button === 0) e.preventDefault()
      }}
      aria-label={
        isActive ? t('aria_recording_active') : t('aria_start_recording')
      }
    >
      <MicrophoneLineIcon size={IconSizes.Size20} color='var(--white)' />

      <AnimatePresence>
        {(isActive || isError || hovered) && (
          <motion.div
            key='content'
            className={styles.content}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 'auto', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            {isActive && <div className={styles.separator} />}

            <AnimatePresence>
              {isActive && (
                <motion.div
                  key='controls'
                  className={styles.controls}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Button
                    icon={MultiplyIcon}
                    isHiddenLabel
                    size={ButtonSizes.S}
                    variant={ButtonVariants.Inverted}
                    color={ButtonColors.Red}
                    onClick={handleCancel}
                  />
                  <canvas
                    ref={canvasRef}
                    className={clsx(styles.canvas, {
                      [styles.canvasVisible]: !isError
                    })}
                    width={WAVEFORM_WIDTH}
                    height={20}
                  />
                  <Button
                    icon={SquareFullIcon}
                    isHiddenLabel
                    size={ButtonSizes.S}
                    variant={ButtonVariants.Ghost}
                    color={ButtonColors.Red}
                    onClick={handleStop}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {isError && (
                <motion.div
                  key='error'
                  className={styles.errorContent}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <ExclamationTriangleLineIcon
                    size={IconSizes.Size14}
                    weight={IconWeight.Bold}
                    color='var(--warning-red-light)'
                  />
                  <span className={styles.errorText}>{t('error_label')}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {!isActive && !isError && hovered && (
                <motion.span
                  key='label'
                  className={styles.label}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  Dictate
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {isFinalizing && <div className={styles.shimmer} />}
    </QuickActionButton>
  )

  const triggerNode = isError ? (
    <Tooltip overlay={error ?? t('error_label')} zIndex={99999}>
      {button}
    </Tooltip>
  ) : (
    button
  )

  return (
    <AnimatePresence>
      {visible && (
        <motion.span
          key='stt-quick-action'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
          <ContextMenu
            triggerOnAction={ContextMenuTriggerOnActions.OnContextMenu}
            showAtClickPoint
            triggerNode={triggerNode}
            options={menuOptions}
            disabled={isActive}
            zIndex={99999}
            isOpen={isContextMenuOpen}
            setIsOpen={v => {
              setIsContextMenuOpen(v)
              if (!v) restoreFocus()
            }}
          />
        </motion.span>
      )}
    </AnimatePresence>
  )
}
